import { useCallback, useMemo, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import QueuePage from '../../components/ui/QueuePage';
import { useApp } from '../../hooks/useApp';

interface ReturnDiscrepancyRow {
  return_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  channel?: string | null;
  quantity?: number | null;
  distributor_name?: string | null;
  created_at?: string | null;
}

function relativeTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ReturnsDiscrepancies(): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer + dist_rep read-only.
  const { isViewer, isDistRep } = useApp();

  const SkuCell = useCallback(
    (p: ICellRendererParams<ReturnDiscrepancyRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (sku) navigate(`/catalog/products/${sku}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate]
  );

  const columns = useMemo<ColDef<ReturnDiscrepancyRow>[]>(
    () =>
      [
        {
          suppressMovable: true,
          resizable: false,
          width: 40,
          maxWidth: 40,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Return #',
          field: 'return_number',
          width: 130,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Channel',
          field: 'channel',
          width: 100,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Qty',
          field: 'quantity',
          width: 70,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Distributor',
          field: 'distributor_name',
          width: 130,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Created',
          field: 'created_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<ReturnDiscrepancyRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<ReturnDiscrepancyRow>[],
    [SkuCell]
  );

  return (
    <QueuePage
      title="Return Discrepancies"
      rpcName="get_returns_queue"
      rpcParams={{ p_status: 'discrepancy' }}
      columns={columns}
      emptyIcon={AlertTriangle}
      emptyMessage="No return discrepancies"
      searchPlaceholder="Search return #, SKU, or title..."
      readOnly={isViewer || isDistRep}
    />
  );
}
